import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const DishCard = ({ dish, onEdit, onDelete, onView }) => {
  const getDietaryIcon = (restriction) => {
    const icons = {
      vegetarian: 'Leaf',
      vegan: 'Sprout',
      'gluten-free': 'Wheat',
      'dairy-free': 'Milk',
      'nut-free': 'Nut',
      halal: 'Moon',
      kosher: 'Star'
    };
    return icons?.[restriction] || 'AlertCircle';
  };

  const getCuisineColor = (cuisine) => {
    const colors = {
      italian: 'bg-red-100 text-red-800',
      french: 'bg-blue-100 text-blue-800',
      asian: 'bg-yellow-100 text-yellow-800',
      mexican: 'bg-orange-100 text-orange-800',
      indian: 'bg-purple-100 text-purple-800',
      mediterranean: 'bg-green-100 text-green-800',
      american: 'bg-gray-100 text-gray-800'
    };
    return colors?.[cuisine?.toLowerCase()] || 'bg-gray-100 text-gray-800';
  };

  const formatLastUsed = (date) => {
    const now = new Date();
    const lastUsed = new Date(date);
    const diffTime = Math.abs(now - lastUsed);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months ago`;
    return `${Math.ceil(diffDays / 365)} years ago`;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-warm hover:shadow-warm-md transition-warm overflow-hidden">
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={dish?.image}
          alt={dish?.imageAlt}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 right-3 flex space-x-1">
          {dish?.dietaryRestrictions?.map((restriction) => (
            <div
              key={restriction}
              className="bg-white/90 backdrop-blur-sm rounded-full p-1.5"
              title={restriction}
            >
              <Icon name={getDietaryIcon(restriction)} size={14} className="text-primary" />
            </div>
          ))}
        </div>
        {dish?.isNew && (
          <div className="absolute top-3 left-3 bg-accent text-accent-foreground px-2 py-1 rounded-full text-xs font-medium">
            New
          </div>
        )}
      </div>
      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-foreground text-lg leading-tight">{dish?.name}</h3>
          <div className="flex items-center space-x-1 ml-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onView(dish)}
              className="p-1 h-auto"
            >
              <Icon name="Eye" size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(dish)}
              className="p-1 h-auto"
            >
              <Icon name="Edit2" size={16} />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(dish)}
              className="p-1 h-auto text-error hover:text-error"
            >
              <Icon name="Trash2" size={16} />
            </Button>
          </div>
        </div>

        {/* Cuisine and Prep Time */}
        <div className="flex items-center space-x-2 mb-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCuisineColor(dish?.cuisine)}`}>
            {dish?.cuisine}
          </span>
          <div className="flex items-center text-muted-foreground text-sm">
            <Icon name="Clock" size={14} className="mr-1" />
            <span>{dish?.prepTime} min</span>
          </div>
          <div className="flex items-center text-muted-foreground text-sm">
            <Icon name="Users" size={14} className="mr-1" />
            <span>{dish?.servings}</span>
          </div>
        </div>

        {/* Ingredients Preview */}
        <div className="mb-3">
          <p className="text-sm text-muted-foreground mb-1">Main ingredients:</p>
          <div className="flex flex-wrap gap-1">
            {dish?.ingredients?.slice(0, 3)?.map((ingredient, index) => (
              <span
                key={index}
                className="bg-muted text-muted-foreground px-2 py-1 rounded text-xs"
              >
                {ingredient}
              </span>
            ))}
            {dish?.ingredients?.length > 3 && (
              <span className="text-muted-foreground text-xs px-2 py-1">
                +{dish?.ingredients?.length - 3} more
              </span>
            )}
          </div>
        </div>

        {/* Usage Stats */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center text-muted-foreground">
            <Icon name="BarChart3" size={14} className="mr-1" />
            <span>Used {dish?.usageCount} times</span>
          </div>
          {dish?.lastUsed && (
            <div className="flex items-center text-muted-foreground">
              <Icon name="Calendar" size={14} className="mr-1" />
              <span>{formatLastUsed(dish?.lastUsed)}</span>
            </div>
          )}
        </div>

        {/* Rating */}
        {dish?.rating && (
          <div className="flex items-center mt-2">
            <div className="flex items-center">
              {[1, 2, 3, 4, 5]?.map((star) => (
                <Icon
                  key={star}
                  name="Star"
                  size={14}
                  className={star <= dish?.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
                />
              ))}
            </div>
            <span className="text-sm text-muted-foreground ml-2">({dish?.rating}/5)</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DishCard;